import React, { useCallback, useMemo, useState } from 'react';
import * as Yup from 'yup';
import { Field, FieldProps, Form, Formik } from 'formik';
import { ErrorMsg } from '@app/form';
import { PaymentMethodsList } from './PaymentMethodsList';
import { AddPaymentMethodButton } from './AddPaymentMethodButton';

interface Props {
  organizationId: string;
  onSuccess?: (paymentMethodId: string) => any;
  submitButton: React.ReactNode;
  defaultPaymentMethodId?: string;
  sessionId?: string;
}

const validationSchema = Yup.object().shape({
  paymentMethodId: Yup.string().required(),
});

export const PaymentMethodStep = ({
  organizationId,
  onSuccess,
  submitButton,
  defaultPaymentMethodId,
  sessionId,
}: Props) => {
  const [initialValues, setInitialValue] = useState({
    paymentMethodId: defaultPaymentMethodId,
  });

  const [addedPaymentMethods, setAddedPaymentMethods] = React.useState<
    string[]
  >([]);
  const onNewPaymentMethod = useCallback(
    (pm: string) => {
      setInitialValue({ paymentMethodId: pm });
      setAddedPaymentMethods((prev) => [...prev, pm]);
    },
    [setInitialValue, setAddedPaymentMethods],
  );

  const onSubmit = useCallback(
    async (values: { paymentMethodId: string | undefined }) => {
      if (onSuccess && values.paymentMethodId) {
        await onSuccess(values.paymentMethodId);
      }
    },
    [onSuccess],
  );

  return (
    <>
      <AddPaymentMethodButton
        organizationId={organizationId}
        selectableStyle
        sessionId={sessionId}
        onSuccess={onNewPaymentMethod}
      />
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
        key={initialValues.paymentMethodId}
      >
        <Form>
          <Field name="paymentMethodId">
            {({ form, meta }: FieldProps) => (
              <PaymentMethodsList
                organizationId={organizationId}
                onSelect={(id) => form.setFieldValue('paymentMethodId', id)}
                selected={meta.value}
                sessionId={sessionId}
                justAdded={addedPaymentMethods}
              />
            )}
          </Field>
          <ErrorMsg name="paymentMethodId" />
          {submitButton}
        </Form>
      </Formik>
    </>
  );
};
